import { MenuFixedItems, ToolbarNavRoot } from 'features/toolbar/toolbar/styles';
import CameraControl from 'features/toolbar/toolbar/controls/CameraControl';
import MicControl from 'features/toolbar/toolbar/controls/MicControl';
import ScreenshareControl from 'features/toolbar/toolbar/controls/ScreenshareControl';
import SettingsControl from 'features/toolbar/toolbar/controls/settings/SettingsControl';
import EndCallControl from 'features/toolbar/toolbar/controls/end-call/EndCallControl';
import GlobalAudioMuteControl from 'features/toolbar/toolbar/controls/global-audio-mute/GlobalAudioMuteControl';
import LeaveBreakoutControl from 'features/toolbar/toolbar/controls/leave-breakout/LeaveBreakoutControl';
import { ToolbarOrientation } from 'features/toolbar/types';
import { usePermissions } from 'features/permissions/usePermissions';
import { PermissionTypes } from 'features/permissions/types';
import { useFeatureFlag } from 'features/feature-set/useFeatureFlag';
import { Fragment } from 'react';
import { useAppSelector } from 'store/hooks';
import { selectDeviceType } from 'features/application/applicationSlice';
import OverflowMenuContainer from 'features/toolbar/OverflowMenuContainer';
import { selectMobileScreenshare } from 'features/sdk/sdkStateSlice';
import LockControl from 'features/toolbar/toolbar/controls/lock/LockControl';
import { hasDisplayMediaSupport } from 'features/user-media/utils';

type DesktopMenuProps = {
  orientation: ToolbarOrientation;
};

const ToolbarNav = ({ orientation }: DesktopMenuProps) => {
  const deviceType = useAppSelector(selectDeviceType);
  const mobileScreenshare = useAppSelector(selectMobileScreenshare);

  const { hasPermissions } = usePermissions();

  const allowVideoBroadcast = hasPermissions(PermissionTypes.broadcastVideo);
  const allowAudioBroadcast = hasPermissions(PermissionTypes.broadcastAudio);

  const screenshareAvailable = useFeatureFlag('screenshare');
  const allowScreenshare = hasPermissions(PermissionTypes.screenshare);

  const globalAudioMuteAvailable = useFeatureFlag('muteSound');

  const primaryMenuItems = [];

  if (allowVideoBroadcast || allowAudioBroadcast) {
    primaryMenuItems.push(
      <Fragment key="broadcast-controls">
        {allowVideoBroadcast && <CameraControl />}
        {allowAudioBroadcast && <MicControl />}
      </Fragment>
    );
  }

  if (globalAudioMuteAvailable) {
    primaryMenuItems.push(<GlobalAudioMuteControl key="global-audio-mute" />);
  }

  const canCaptureDisplay = deviceType === 'desktop' && hasDisplayMediaSupport();

  if (screenshareAvailable && allowScreenshare && (canCaptureDisplay || mobileScreenshare)) {
    primaryMenuItems.push(<ScreenshareControl key="screenshare" />);
  }

  return (
    <ToolbarNavRoot orientation={orientation}>
      {primaryMenuItems.length ? (
        <MenuFixedItems orientation={orientation}>{primaryMenuItems}</MenuFixedItems>
      ) : null}
      <OverflowMenuContainer leading={primaryMenuItems.length === 0} />
      <MenuFixedItems orientation={orientation}>
        <LockControl />
        <SettingsControl />
        <LeaveBreakoutControl />
        <EndCallControl />
      </MenuFixedItems>
    </ToolbarNavRoot>
  );
};

export default ToolbarNav;
